import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { style } from './zui-button-dropdown-css.js';
import './zui-button.js';
import type { ZuiButton, ZuiButtonType } from './zui-button.js';
import '@zywave/zui-icons';
import type { PropertyValues } from 'lit';
import type { ZuiIcon } from '@zywave/zui-icons';

/**
 * A component that uses a `<zui-button>` to open a dropdown menu of options.
 *
 * @element zui-button-dropdown
 *
 * @slot - Default, unnamed slot; for inserting label text or an icon into `<zui-button-dropdown>`
 * @slot option - For each dropdown menu option, apply this slot attribute to your slotted menu item; e.g., `<button slot="option">`
 *
 * @csspart menu - The menu container that reveals when you click `<zui-button-dropdown>`; this is exposed as a CSS shadow part and can be accessed with `::part(menu)`
 */
export class ZuiButtonDropdown extends ZuiBaseElement {
  /**
   * This attribute will decide whether the submenu is open or not
   */
  @property({ type: Boolean, reflect: true })
  open = false;
  /**
   *This attribute will decide whether you want to disable the `<zui-button>` dropdown trigger
   */
  @property({ type: Boolean, reflect: true })
  disabled = false;

  /**
   * Use this attribute to set the type of button dropdown you want. See [Buttons](https://booster.zywave.dev/design-system/components/buttons/) for button style variations.
   * @type { primary | secondary | link }
   */
  @property({ type: String, reflect: true })
  type: ZuiButtonType = 'primary';

  /**
   * Use this attribute when you want an icon-only button dropdown
   */
  @property({ type: Boolean, reflect: true })
  icon = false;

  @query('zui-icon')
  private _chevronIcon: ZuiIcon;

  @query('zui-button')
  private _zuiButton: ZuiButton;

  static get styles() {
    return [super.styles, style];
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        // if dropdown menu is open
        if (this.open) {
          // open menu, hitting 'Shift + Tab' closes menu
          if (e.shiftKey && e.code === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            this.#toggleDropdown();
          }

          // open menu, hitting 'Tab' closes menu and focuses on zui-button-dropdown
          if (e.code === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            this.#toggleDropdown();
            (this.shadowRoot!.querySelector('zui-button') as HTMLElement).focus();
          }

          // open menu, hitting 'Space' || 'Enter' selects menu item, closes menu and then focuses zui-button-dropdown
          if (e.code === 'Enter' || e.code === 'Space') {
            e.preventDefault();
            (document.activeElement as HTMLElement).click();
            (this.shadowRoot!.querySelector('zui-button') as HTMLElement).focus();
          }

          // open menu, hitting 'Escape' closes menu
          e.code === 'Escape' && this.#toggleDropdown();

          // arrow keys toggle through menu options
          const navigationKeys = ['ArrowUp', 'ArrowDown'];

          if (navigationKeys.includes(e.code)) {
            const selectableOptions: HTMLElement[] = Array.from(
              this.querySelectorAll('a:not([disabled]),button:not([disabled])')
            );

            const activeElement = document.activeElement;
            let foundIndex = selectableOptions.findIndex((el) => {
              return el === activeElement;
            });

            if (foundIndex !== -1) {
              if (e.code === 'ArrowDown') {
                const nextIndex = foundIndex + 1;
                foundIndex = nextIndex === selectableOptions.length ? selectableOptions.length - 1 : nextIndex;
              } else {
                const previousIndex = foundIndex - 1;
                foundIndex = previousIndex < 0 ? 0 : previousIndex;
              }
            } else {
              foundIndex = 0;
            }
            selectableOptions[foundIndex].focus();
            e.preventDefault();
            e.stopPropagation();
          }
        } // end if (this.open)

        // hitting 'Space' or 'Enter' will toggle dropdown open/closed
        const selectionKeys = ['Space', 'Enter'];
        selectionKeys.includes(e.code) && this.#toggleDropdown();
      },
      { capture: true }
    ); // end 'keydown' eventListener

    document.addEventListener('click', (e) => {
      const clickedWithinButtonDropdown = e.composedPath().includes(this);
      if (!clickedWithinButtonDropdown) {
        this.open = false;
      }
    });
  }

  protected updated(changedProps: PropertyValues) {
    super.updated(changedProps);

    if (this._chevronIcon) {
      this._chevronIcon.style.transition = 'transform .3s ease-out';
      this._chevronIcon.style.transform = this.open ? 'rotate(-180deg)' : '';
    }

    // DEPRECATED: type="icon" and type="icon-only"
    if (this.type.includes('icon') || this.type.includes('icon-only') || this.icon) {
      this._zuiButton.setAttribute('icon', '');
    }

    // DEPRECATED: type="icon-right"
    if (this.type.includes('icon-right') || this.hasAttribute('icon-right')) {
      this._zuiButton.setAttribute('icon-right', '');
    }

    // DEPRECATED: type="danger"
    if (this.type.includes('danger') || this.hasAttribute('danger')) {
      this._zuiButton.setAttribute('danger', '');
    }
  }

  render() {
    return html`
      <zui-button type="${this.#getButtonType()}" @click="${this.#toggleDropdown}" ?disabled="${this.disabled}"
        ><slot></slot>${this.icon || this.type.includes('icon')
          ? html``
          : html`<zui-icon icon="zui-down"></zui-icon></zui-button>`}
      </zui-button>
      <div part="menu" class="menu"><slot name="option" @click="${() => (this.open = false)}"></slot></div>
    `;
  }

  #getButtonType() {
    if (this.type.includes('primary')) {
      return 'primary';
    } else if (this.type.includes('secondary')) {
      return 'secondary';
    } else if (this.type.includes('link')) {
      return 'link';
    } else {
      return '';
    }
  }

  #toggleDropdown() {
    this.open = !this.open;
  }
}

window.customElements.define('zui-button-dropdown', ZuiButtonDropdown);

declare global {
  interface HTMLElementTagNameMap {
    'zui-button-dropdown': ZuiButtonDropdown;
  }
}
